/**
 * SettingToggle component
 *
 * Renders a single notification setting with a toggle switch.
 * Includes label, optional description, nested children, and disabled state support.
 */

import { Box, Typography, Switch, FormControlLabel } from '@mui/material';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import type { ReactNode } from 'react';

interface SettingToggleProps {
  /** Label text for the setting */
  label: string;
  /** Optional description text shown below the label */
  description?: string;
  /** Whether the toggle is currently checked */
  checked: boolean;
  /** Callback when toggle state changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled (cannot be changed) */
  disabled?: boolean;
  /** Nested controls rendered under the toggle (e.g. digest schedule) */
  children?: ReactNode;
}

/**
 * Reusable toggle component for notification settings
 *
 * Features:
 * - Clear label and description layout
 * - Optimistic updates (no disabled state to prevent flicker)
 * - Capital Design System styling
 */
export function SettingToggle({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  children,
}: SettingToggleProps) {
  return (
    <Box
      sx={{
        py: 2,
        px: 3,
        borderBottom: `1px solid ${capitalDesignTokens.semanticColors.border.primary}`,
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label={label}
        labelPlacement="start"
        disabled={disabled}
        sx={{
          m: 0,
          width: '100%',
          justifyContent: 'space-between',
          '& .MuiFormControlLabel-label': { fontWeight: 500 },
        }}
      />
      {description && (
        <Typography
          variant="body2"
          color={disabled ? 'text.disabled' : 'text.secondary'}
          sx={{ mt: 0.5 }}
        >
          {description}
        </Typography>
      )}
      {children}
    </Box>
  );
}
